import { FC, useState, ReactElement, useRef } from 'react'
import { useIntl } from 'react-intl'
import {
  IconButton,
  SearchField,
  FilterIcon,
  useOnClickOutside,
  ISearchFieldProps,
} from '@instabox/consumer-web-ui'

import FAQFilter, { Filter, FilterActions } from './FAQFilter'
import styles from './faqSearch.module.scss'

export type FAQSearchProps = ISearchFieldProps &
  FilterActions & {
    filters: Filter[]
    onValueChange: (value: string) => void
    children: ReactElement[]
  }

const FAQSearch: FC<FAQSearchProps> = ({
  children,
  value,
  showResults,
  filters,
  onValueChange,
  onClose,
  onFilterApply,
  onFilterReset,
  ...props
}) => {
  const [filterOpen, setFilterOpen] = useState(false)
  const { formatMessage } = useIntl()
  const applyFilters = () => {
    setFilterOpen(false)
    onFilterApply()
  }
  const filterContainerRef = useRef<HTMLDivElement>(null)

  useOnClickOutside(
    filterContainerRef,
    () => {
      if (filterOpen) {
        setFilterOpen(false)
      }
    },
    'mouseup',
  )

  const resetFilters = () => {
    setFilterOpen(false)
    onFilterReset()
  }

  const filterEnabled = filterOpen || filters.some(filter => !!filter.value)

  return (
    <div className={styles.searchContainer}>
      <SearchField
        value={value}
        placeholder={formatMessage({ id: 'help.search.input.placeholder' })}
        showResults={showResults && !filterOpen}
        size="large"
        onChange={e => onValueChange(e.target.value)}
        onClose={onClose}
        iconButtonAfter={
          <IconButton
            label={formatMessage({ id: 'help.search.input.open_filter' })}
            variant={filterEnabled ? 'info' : 'secondary'}
            icon={<FilterIcon />}
            onClick={() => setFilterOpen(true)}
          />
        }
        {...props}>
        {children}
      </SearchField>
      <div className="row justify-content-end">
        <div className="col-12 col-md-6 col-lg-5 col-xl-4 col-xxl-3" style={{ zIndex: 1 }}>
          <FAQFilter
            filters={filters}
            isOpen={filterOpen}
            ref={filterContainerRef}
            onFilterApply={applyFilters}
            onFilterReset={resetFilters}
          />
        </div>
      </div>
    </div>
  )
}

export default FAQSearch
